// 職責：讀取 JWT 的到期時間。**不驗簽名** —— 驗簽名是後端的事，這裡只是想知道「還能不能用」。

/*
 * 為什麼要有這個檔（2026-09-18 浸會 801 上載相片失敗）：
 *
 *   門店 JWT 得 8 個鐘命（webapi `JwtUtils.cs`：`Expires = DateTime.Now.AddHours(8)`），
 *   而後端**只有兩支 API 套了 `[Authorize]`**（`users` 與 `FileUpload`），其餘一律不驗 token。
 *   所以一張過了期的 token 在這個 app 內**完全沒有病徵**：餐牌照改、設定照存、報表照看，
 *   直到有人按「上載相片」才收到一個 401。
 *
 *   真實個案：現場那部 Edge 開機還原了前一日的 `localStorage`（機上 nginx log 14 日內
 *   一次 `users/authenticate` 都沒有），該 app 假裝完全登入了，上載連按四次全部 401。
 *   使用者只會覺得「上載壞了」，不會想到「我未登入」。
 *
 *   ⇒ 還原 session 時一定要一併驗證 `exp`。這是唯一不需要呼叫後端就知道 token 已失效的方法。
 *
 * 🚨 解析不到／沒有 `exp` 一律回 `null` ＝「不知道何時失效」，呼叫端要當它**仍然有效**：
 *    寧願讓一個 401 提醒（可以重新登入），也好過因為一個 base64 解碼細節把所有人踢出去。
 */

/** JWT payload 內與到期有關的欄；其餘一概不理 */
interface JwtExpiryClaims {
  /** 到期時間（Unix 秒，RFC 7519 §4.1.4）*/
  exp?: unknown
}

/** base64url → UTF-8 字串。壞資料回 null，不拋錯 */
function decodeSegment(segment: string): string | null {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    // atob 要求長度是 4 的倍數，但 JWT 的 base64url 會剝走 `=`
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const binary = atob(padded)
    // payload 有中文（例如全名）就不可以當 latin1 直接用
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  } catch {
    return null
  }
}

/**
 * 回傳 JWT 的到期時間（毫秒 epoch）。沒有 `exp`、不是 JWT、解析不到 ⇒ `null`。
 *
 * ⚠️ 只讀 payload，沒有驗簽名 —— 這個值只用來決定「需不需要叫人重新登入」，
 *    不可以用來做任何授權判斷。
 */
export function jwtExpiryMs(token: string): number | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const json = decodeSegment(parts[1] ?? '')
  if (json === null) return null
  try {
    const payload: unknown = JSON.parse(json)
    if (typeof payload !== 'object' || payload === null) return null
    const { exp } = payload as JwtExpiryClaims
    // 後端有機會寫成字串（`"exp": "1789..."`）—— 兩種都收
    const seconds = typeof exp === 'number' ? exp : typeof exp === 'string' ? Number(exp) : NaN
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null
  } catch {
    return null
  }
}

/**
 * 該 token 是否已經過期。解析不到到期時間 ⇒ **當它仍然有效**（見檔頭）。
 *
 * ⚠️ 沒有留任何 clock skew：後端 `JwtMiddleware` 寫死 `ClockSkew = TimeSpan.Zero`，
 *    我們寬鬆過它就會出現「前端說仍有效、後端照樣 401」的盲區。
 */
export function isJwtExpired(token: string, now: number = Date.now()): boolean {
  const expiry = jwtExpiryMs(token)
  return expiry !== null && expiry <= now
}
